import type { ReactNode } from "react";
import { ChevronsUpDown } from "lucide-react";

import { CodeText, EmptyState, InfoTooltip, JsonBlock, SectionLabel } from "@/components/common";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import type { SocialPostGenerationTaskDetail } from "../types";

type ImageCandidateGroupList = SocialPostGenerationTaskDetail["imageCandidateGroups"];
type ImageCandidateGroup = ImageCandidateGroupList[number];
type ImageCandidateAsset = ImageCandidateGroup["assets"][number];
type ImageQaDiagnosticList = SocialPostGenerationTaskDetail["imageQaDiagnostics"];
type ImageQaDiagnostic = ImageQaDiagnosticList[number];

export function getImageCandidateStatusLabel(
  status: ImageCandidateGroup["selectionStatus"] | ImageCandidateAsset["selectionStatus"],
): string {
  switch (status) {
    case "qa_passed":
      return "QA passed";
    case "qa_failed_selected_best":
      return "Selected best available";
    case "qa_unavailable_selected_best":
      return "QA unavailable";
    case "qa_disabled":
      return "QA disabled";
    default:
      return "No selection status";
  }
}

export function getSelectedImageCandidateAsset(
  groups: ImageCandidateGroupList,
  blockId: string,
  carouselSlideId?: string | null,
): ImageCandidateAsset | null {
  const group = carouselSlideId
    ? groups.find(
        (candidateGroup) =>
          candidateGroup.blockId === blockId &&
          candidateGroup.carouselSlide?.id === carouselSlideId,
      )
    : groups.find((candidateGroup) => candidateGroup.blockId === blockId);
  if (!group) {
    return null;
  }

  return (
    group.assets.find(
      (asset) => asset.generatedAssetId === group.selectedGeneratedAssetId && asset.selected,
    ) ??
    group.assets.find((asset) => asset.selected && asset.assetVariant === "derivative") ??
    group.assets.find((asset) => asset.selected) ??
    null
  );
}

const formatScore = (score: number | null | undefined): string =>
  typeof score === "number" ? `${score}%` : "--";

type ImageCandidateComparison = {
  groupKey: string;
  candidateId: string;
  candidateIndex: number;
  attemptIndex: number;
  assets: ImageCandidateAsset[];
  primaryAsset: ImageCandidateAsset;
  detailAsset: ImageCandidateAsset;
  selected: boolean;
  isLinkedWinner: boolean;
  selectionStatus: ImageCandidateAsset["selectionStatus"];
  selectionReason: string | null;
  qaResult: ImageCandidateAsset["qaResult"];
};

const getAssetLabel = (asset: ImageCandidateAsset): string =>
  asset.assetVariant === "master"
    ? "Master"
    : asset.assetVariant === "derivative"
      ? "Derivative"
      : "Asset";

const formatDimensions = (asset: ImageCandidateAsset): string =>
  `${asset.width ?? "?"} x ${asset.height ?? "?"}`;

const getAssetVariantDescription = (asset: ImageCandidateAsset): string =>
  asset.assetVariant === "master"
    ? "Master assets are the original generated images kept for traceability and alternate review."
    : asset.assetVariant === "derivative"
      ? "Derivative assets are the cropped or rendered variants used by the final scene when selected."
      : "This generated asset is kept with the candidate for review.";

const imageCandidateAssetRank = (asset: ImageCandidateAsset): number =>
  asset.assetVariant === "derivative" ? 0 : asset.assetVariant === "master" ? 1 : 2;

const sortImageCandidateAssets = (left: ImageCandidateAsset, right: ImageCandidateAsset): number =>
  imageCandidateAssetRank(left) - imageCandidateAssetRank(right) ||
  left.createdAt.localeCompare(right.createdAt) ||
  left.generatedAssetId.localeCompare(right.generatedAssetId);

const getCandidateTitle = (candidate: ImageCandidateComparison): string =>
  `Candidate ${candidate.candidateIndex}`;

const getCandidateCoordinate = (asset: ImageCandidateAsset): string =>
  `${asset.candidateIndex}:${asset.attemptIndex}`;

const getImageCandidateGroupTitle = (group: ImageCandidateGroup): string => {
  if (!group.carouselSlide) {
    return group.blockId;
  }

  return `Slide ${group.carouselSlide.index} · ${group.carouselSlide.role} · ${group.blockId}`;
};

const buildImageCandidateComparisons = (group: ImageCandidateGroup): ImageCandidateComparison[] => {
  const candidateCoordinatesById = new Map<string, Set<string>>();
  for (const asset of group.assets) {
    const coordinates = candidateCoordinatesById.get(asset.candidateId);
    if (coordinates) {
      coordinates.add(getCandidateCoordinate(asset));
    } else {
      candidateCoordinatesById.set(asset.candidateId, new Set([getCandidateCoordinate(asset)]));
    }
  }

  const groupByCandidateCoordinate = Array.from(candidateCoordinatesById.values()).some(
    (coordinates) => coordinates.size > 1,
  );
  const assetsByCandidateKey = new Map<string, ImageCandidateAsset[]>();
  for (const asset of group.assets) {
    const groupKey = groupByCandidateCoordinate ? getCandidateCoordinate(asset) : asset.candidateId;
    const assets = assetsByCandidateKey.get(groupKey);
    if (assets) {
      assets.push(asset);
    } else {
      assetsByCandidateKey.set(groupKey, [asset]);
    }
  }

  return Array.from(assetsByCandidateKey.entries())
    .map(([groupKey, candidateAssets]) => {
      const assets = candidateAssets.toSorted(sortImageCandidateAssets);
      const primaryAsset =
        assets.find((asset) => asset.assetVariant === "derivative") ?? assets[0]!;
      const selectedAsset =
        assets.find((asset) => asset.generatedAssetId === group.selectedGeneratedAssetId) ??
        assets.find((asset) => asset.selected && asset.assetVariant === "derivative") ??
        assets.find((asset) => asset.selected) ??
        null;
      const detailAsset = selectedAsset ?? primaryAsset;
      const qaAsset =
        (detailAsset.qaResult ? detailAsset : null) ??
        assets.find((asset) => asset.selected && asset.qaResult) ??
        primaryAsset;
      const selected = assets.some((asset) => asset.selected);

      return {
        groupKey,
        candidateId: primaryAsset.candidateId,
        candidateIndex: primaryAsset.candidateIndex,
        attemptIndex: primaryAsset.attemptIndex,
        assets,
        primaryAsset,
        detailAsset,
        selected,
        isLinkedWinner: assets.some(
          (asset) => asset.generatedAssetId === group.selectedGeneratedAssetId,
        ),
        selectionStatus: detailAsset.selectionStatus ?? (selected ? group.selectionStatus : null),
        selectionReason: detailAsset.selectionReason,
        qaResult: qaAsset.qaResult,
      } satisfies ImageCandidateComparison;
    })
    .toSorted(
      (left, right) =>
        left.candidateIndex - right.candidateIndex || left.attemptIndex - right.attemptIndex,
    );
};

const getImageCandidateComparisonCount = (group: ImageCandidateGroup): number =>
  buildImageCandidateComparisons(group).length;

const formatDiagnosticTimestamp = (createdAt: string): string =>
  createdAt.replace("T", " ").replace(/\.\d{3}Z$/u, "Z");

const qaDiagnosticMatchesGroup = (
  diagnostic: ImageQaDiagnostic,
  group: ImageCandidateGroup,
): boolean => {
  if (diagnostic.candidateGroupId) {
    return diagnostic.candidateGroupId === group.candidateGroupId;
  }

  if (diagnostic.candidateId) {
    return group.assets.some((asset) => asset.candidateId === diagnostic.candidateId);
  }

  return Boolean(diagnostic.blockId && diagnostic.blockId === group.blockId);
};

const formatQaDiagnosticContext = (diagnostic: ImageQaDiagnostic): string => {
  const parts = [
    diagnostic.blockId ? `block ${diagnostic.blockId}` : null,
    diagnostic.candidateIndex ? `candidate ${diagnostic.candidateIndex}` : null,
    diagnostic.attemptIndex !== null ? `attempt ${diagnostic.attemptIndex}` : null,
    diagnostic.qaThresholdPct !== null ? `threshold ${diagnostic.qaThresholdPct}%` : null,
  ].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(" · ") : "task-level QA event";
};

function CandidateBadge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "good" | "warn";
}) {
  const toneClass =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-border bg-muted text-muted-foreground";

  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneClass}`}>
      {children}
    </span>
  );
}

function CandidateBadgeWithInfo({
  children,
  content,
  label,
  tone = "neutral",
}: {
  children: ReactNode;
  content: ReactNode;
  label: string;
  tone?: "neutral" | "good" | "warn";
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <CandidateBadge tone={tone}>{children}</CandidateBadge>
      <InfoTooltip
        label={label}
        content={content}
        className="size-4 border-transparent bg-transparent"
      />
    </span>
  );
}

function CandidateDisclosure({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Collapsible className="rounded-xl border bg-background">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-semibold text-foreground transition-colors hover:bg-muted/30">
        <span>{title}</span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t p-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function QaDiagnosticRow({ diagnostic }: { diagnostic: ImageQaDiagnostic }) {
  const failed = !diagnostic.success;
  const candidateIdPrefix = diagnostic.candidateId?.slice(0, 8);
  const groupIdPrefix = diagnostic.candidateGroupId?.slice(0, 8);

  return (
    <div
      className={`space-y-2 rounded-xl border px-3 py-2 text-xs ${
        failed ? "border-amber-200 bg-amber-50/80" : "bg-background"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <CandidateBadge tone={failed ? "warn" : "good"}>
              {failed ? "QA call failed" : "QA call completed"}
            </CandidateBadge>
            <CodeText>
              {diagnostic.provider}/{diagnostic.model}
            </CodeText>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {formatQaDiagnosticContext(diagnostic)}
          </div>
        </div>
        <CodeText className="shrink-0">{formatDiagnosticTimestamp(diagnostic.createdAt)}</CodeText>
      </div>

      {failed ? (
        <div className="rounded-md bg-background/70 px-2 py-1.5 text-[11px] leading-5 text-amber-950">
          <span className="font-semibold">{diagnostic.errorName ?? "Provider error"}</span>
          {diagnostic.errorMessage ? <span>: {diagnostic.errorMessage}</span> : null}
        </div>
      ) : null}

      {candidateIdPrefix || groupIdPrefix || diagnostic.traceId ? (
        <div className="flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
          {groupIdPrefix ? <CodeText>group {groupIdPrefix}</CodeText> : null}
          {candidateIdPrefix ? <CodeText>candidate {candidateIdPrefix}</CodeText> : null}
          {diagnostic.traceId ? <CodeText>trace {diagnostic.traceId}</CodeText> : null}
        </div>
      ) : null}
    </div>
  );
}

function QaDiagnosticsDisclosure({
  diagnostics,
  title,
}: {
  diagnostics: ImageQaDiagnostic[];
  title: string;
}) {
  if (diagnostics.length === 0) {
    return null;
  }

  const failureCount = diagnostics.filter((diagnostic) => !diagnostic.success).length;

  return (
    <Collapsible className="rounded-2xl border bg-card">
      <div className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-muted/30">
        <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-3 text-left text-xs font-semibold text-foreground">
          <span>{title}</span>
          <span className="flex items-center gap-2 text-muted-foreground">
            <CodeText>
              {diagnostics.length} QA event{diagnostics.length === 1 ? "" : "s"}
              {failureCount > 0 ? ` · ${failureCount} failed` : ""}
            </CodeText>
            <ChevronsUpDown className="size-3.5 shrink-0" aria-hidden="true" />
          </span>
        </CollapsibleTrigger>
        <InfoTooltip
          label={`Explain ${title.toLowerCase()}`}
          content="Provider usage events recorded while scoring image candidates. Open this when QA is unavailable or when you need the exact model, error, and candidate coordinates without inspecting raw JSON."
        />
      </div>
      <CollapsibleContent className="border-t p-3">
        <div className="grid gap-2">
          {diagnostics.map((diagnostic) => (
            <QaDiagnosticRow key={diagnostic.id} diagnostic={diagnostic} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function PromptPanel({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return null;
  }

  return (
    <div className="space-y-1">
      <SectionLabel className="text-[10px] tracking-wide">{label}</SectionLabel>
      <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 whitespace-pre-wrap break-words text-[11px] leading-5 text-foreground">
        {value}
      </div>
    </div>
  );
}

function CandidateAssetLink({ asset }: { asset: ImageCandidateAsset }) {
  const assetLabel = getAssetLabel(asset);

  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border bg-background px-2.5 py-2 text-[11px]">
      <a
        href={asset.signedUrl}
        target="_blank"
        rel="noreferrer"
        className="flex min-w-0 flex-1 items-center justify-between gap-2 transition-colors hover:text-foreground"
      >
        <span className="font-medium text-foreground">{assetLabel} asset</span>
        <CodeText className="shrink-0" numeric>
          {formatDimensions(asset)}
        </CodeText>
      </a>
      <InfoTooltip
        label={`Explain ${assetLabel.toLowerCase()} asset`}
        content={getAssetVariantDescription(asset)}
        className="size-4 border-transparent bg-transparent"
      />
    </div>
  );
}

function QaResultPanel({ qaResult }: { qaResult: ImageCandidateAsset["qaResult"] }) {
  const failures = qaResult?.failures ?? [];

  if (!qaResult) {
    return (
      <div className="rounded-md border border-dashed px-2 py-3 text-[11px] text-muted-foreground">
        No QA result recorded for this asset.
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-2">
      <div className="grid gap-1.5 text-[11px] text-muted-foreground sm:grid-cols-2">
        <span>Brand fit {formatScore(qaResult.scores.brandFitPct)}</span>
        <span>Prompt {formatScore(qaResult.scores.promptAdherencePct)}</span>
        <span>Story {formatScore(qaResult.scores.storyMatchPct)}</span>
        <span>Composition {formatScore(qaResult.scores.compositionPct)}</span>
        <span>Negative space {formatScore(qaResult.scores.negativeSpacePct)}</span>
        <span>Artifact risk {formatScore(qaResult.scores.artifactRiskPct)}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {failures.length > 0 ? (
          failures.map((failure) => (
            <CandidateBadge key={failure} tone="warn">
              {failure}
            </CandidateBadge>
          ))
        ) : (
          <CandidateBadge tone="good">no failures</CandidateBadge>
        )}
      </div>
      <p className="text-[11px] leading-5 text-foreground">{qaResult.rationale}</p>
    </div>
  );
}

function ImageCandidateCard({ candidate }: { candidate: ImageCandidateComparison }) {
  const asset = candidate.primaryAsset;
  const failures = candidate.qaResult?.failures ?? [];

  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-card shadow-xs ${
        candidate.isLinkedWinner ? "border-emerald-300 ring-2 ring-emerald-100" : ""
      }`}
    >
      <a
        href={asset.signedUrl}
        target="_blank"
        rel="noreferrer"
        className="relative block aspect-[4/3] bg-muted/20 transition-opacity hover:opacity-90"
      >
        <img
          src={asset.signedUrl}
          alt={getCandidateTitle(candidate)}
          className="size-full object-cover"
        />
        <div className="absolute inset-x-2 top-2 flex flex-wrap gap-1.5">
          {candidate.isLinkedWinner ? (
            <CandidateBadge tone="good">linked winner</CandidateBadge>
          ) : null}
          {candidate.selected && !candidate.isLinkedWinner ? (
            <CandidateBadge tone="good">selected</CandidateBadge>
          ) : null}
        </div>
      </a>

      <div className="space-y-3 p-3 text-xs text-muted-foreground">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-foreground">{getCandidateTitle(candidate)}</div>
            <CodeText className="block">
              attempt {candidate.attemptIndex} · {formatDimensions(asset)}
            </CodeText>
          </div>
          <CodeText className="rounded-full bg-muted px-2 py-0.5">
            {candidate.candidateId.slice(0, 8)}
          </CodeText>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <CandidateBadge tone={candidate.selectionStatus === "qa_passed" ? "good" : "neutral"}>
            {getImageCandidateStatusLabel(candidate.selectionStatus)}
          </CandidateBadge>
          <CandidateBadge tone={failures.length > 0 ? "warn" : "neutral"}>
            QA {formatScore(candidate.qaResult?.overallScorePct)}
          </CandidateBadge>
          <CandidateBadge>
            {candidate.assets.length} asset{candidate.assets.length === 1 ? "" : "s"}
          </CandidateBadge>
          {failures.slice(0, 2).map((failure) => (
            <CandidateBadge key={failure} tone="warn">
              {failure}
            </CandidateBadge>
          ))}
        </div>

        {candidate.selectionReason ? (
          <p className="rounded-md bg-muted/30 px-2 py-1.5 text-[11px] leading-5 text-foreground">
            {candidate.selectionReason}
          </p>
        ) : null}

        <div className="grid gap-2 border-t pt-3 sm:grid-cols-2">
          {candidate.assets.map((candidateAsset) => (
            <CandidateAssetLink key={candidateAsset.generatedAssetId} asset={candidateAsset} />
          ))}
        </div>
      </div>
    </article>
  );
}

function SelectedCandidatePanel({ candidate }: { candidate: ImageCandidateComparison }) {
  const asset = candidate.detailAsset;
  const failures = candidate.qaResult?.failures ?? [];

  return (
    <aside className="space-y-3 rounded-2xl border bg-muted/20 p-3">
      <div className="grid gap-4 lg:grid-cols-[minmax(240px,420px)_1fr]">
        <a
          href={asset.signedUrl}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-xl border bg-background transition-opacity hover:opacity-90"
        >
          <img
            src={asset.signedUrl}
            alt={getCandidateTitle(candidate)}
            className="aspect-[4/3] w-full object-cover"
          />
        </a>

        <div className="min-w-0 space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <SectionLabel className="text-[10px] tracking-wide">Selected Result</SectionLabel>
              <InfoTooltip
                label="Explain selected result"
                content="This is the final candidate linked to the scene block. Review it first; comparison data and raw prompts stay collapsed until needed."
              />
            </div>
            <div className="mt-1 flex items-start justify-between gap-3">
              <div>
                <h5 className="text-sm font-semibold text-foreground">
                  {getCandidateTitle(candidate)}
                </h5>
                <CodeText numeric>
                  attempt {candidate.attemptIndex} · {formatDimensions(asset)}
                </CodeText>
              </div>
              <CodeText className="rounded-full bg-background px-2 py-0.5">
                {asset.generatedAssetId.slice(0, 8)}
              </CodeText>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {candidate.isLinkedWinner ? (
              <CandidateBadgeWithInfo
                tone="good"
                label="Explain linked winner"
                content="The selected generated asset id points at this candidate, so this image is what the scene block uses."
              >
                linked winner
              </CandidateBadgeWithInfo>
            ) : null}
            <CandidateBadge tone={candidate.selectionStatus === "qa_passed" ? "good" : "neutral"}>
              {getImageCandidateStatusLabel(candidate.selectionStatus)}
            </CandidateBadge>
            <CandidateBadgeWithInfo
              tone={failures.length > 0 ? "warn" : "neutral"}
              label="Explain QA score"
              content="QA score summarizes automated visual checks for brand fit, prompt adherence, story match, composition, negative space, artifacts, and text or logo policy risk."
            >
              QA {formatScore(candidate.qaResult?.overallScorePct)}
            </CandidateBadgeWithInfo>
          </div>

          {candidate.selectionReason ? (
            <p className="rounded-md bg-background px-2 py-1.5 text-[11px] leading-5 text-foreground">
              {candidate.selectionReason}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-2 lg:grid-cols-2">
        <CandidateDisclosure title="Asset files">
          <div className="grid gap-2">
            {candidate.assets.map((candidateAsset) => (
              <CandidateAssetLink key={candidateAsset.generatedAssetId} asset={candidateAsset} />
            ))}
          </div>
        </CandidateDisclosure>

        <CandidateDisclosure title="QA result">
          <QaResultPanel qaResult={candidate.qaResult} />
        </CandidateDisclosure>

        <CandidateDisclosure title="Prompts">
          <div className="grid gap-2">
            <PromptPanel label="Planner Prompt" value={asset.plannerImageBrief ?? asset.prompt} />
            <PromptPanel label="Realizer Prompt" value={asset.realizerPrompt} />
            <PromptPanel label="Request Prompt" value={asset.requestPrompt} />
          </div>
        </CandidateDisclosure>

        <CandidateDisclosure title="Payload JSON">
          <JsonBlock value={asset.payloadJson} />
        </CandidateDisclosure>
      </div>
    </aside>
  );
}

function ImageCandidateGroupPanel({
  group,
  qaDiagnostics,
}: {
  group: ImageCandidateGroup;
  qaDiagnostics: ImageQaDiagnostic[];
}) {
  const candidates = buildImageCandidateComparisons(group);
  const selectedCandidate =
    candidates.find((candidate) => candidate.isLinkedWinner) ??
    candidates.find((candidate) => candidate.selected) ??
    candidates[0] ??
    null;
  const qaResultCount = group.assets.filter((asset) => asset.qaResult).length;
  const showQaUnavailableWarning = group.qaEnabled && qaResultCount === 0;
  const groupQaDiagnostics = qaDiagnostics.filter((diagnostic) =>
    qaDiagnosticMatchesGroup(diagnostic, group),
  );

  return (
    <div className="space-y-3 rounded-2xl border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-foreground">{getImageCandidateGroupTitle(group)}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <CandidateBadgeWithInfo
              tone={group.selectionStatus === "qa_passed" ? "good" : "neutral"}
              label="Explain selected result status"
              content="Selection status explains how the final result was chosen after QA checks and fallback rules."
            >
              {getImageCandidateStatusLabel(group.selectionStatus)}
            </CandidateBadgeWithInfo>
            <CandidateBadgeWithInfo
              tone={group.qaEnabled ? "good" : "neutral"}
              label="Explain QA mode"
              content="QA mode controls whether generated candidates are scored before the best available image is linked to the scene."
            >
              QA {group.qaEnabled ? "enabled" : "disabled"}
            </CandidateBadgeWithInfo>
            {group.retryEnabled ? (
              <CandidateBadgeWithInfo
                tone="warn"
                label="Explain retry generation"
                content="Retry generation means the planner requested another image attempt after an earlier candidate did not satisfy review criteria."
              >
                {group.retryCount} retry
              </CandidateBadgeWithInfo>
            ) : null}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <CodeText className="block">{group.candidateGroupId.slice(0, 8)}</CodeText>
          <div>
            {candidates.length} candidates · {group.assets.length} assets
          </div>
        </div>
      </div>

      {showQaUnavailableWarning ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
          QA was enabled, but no candidate QA results were recorded. Selection fell back without
          visual scoring.
        </div>
      ) : null}

      <QaDiagnosticsDisclosure diagnostics={groupQaDiagnostics} title="QA diagnostics" />

      {selectedCandidate ? <SelectedCandidatePanel candidate={selectedCandidate} /> : null}

      <Collapsible className="rounded-2xl border bg-card">
        <div className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-muted/30">
          <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-3 text-left text-xs font-semibold text-foreground">
            <span>Compare candidates</span>
            <span className="flex items-center gap-2 text-muted-foreground">
              <CodeText>
                {candidates.length} candidates · {group.assets.length} assets
              </CodeText>
              <ChevronsUpDown className="size-3.5 shrink-0" aria-hidden="true" />
            </span>
          </CollapsibleTrigger>
          <InfoTooltip
            label="Explain candidate comparison"
            content="Open this only when you need to inspect non-selected candidates, failure tags, alternate assets, and selection trace details."
          />
        </div>
        <CollapsibleContent className="border-t p-3">
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {candidates.map((candidate) => (
              <ImageCandidateCard key={candidate.groupKey} candidate={candidate} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function ImageCandidateGroups({
  groups,
  qaDiagnostics = [],
  blockId,
  carouselSlideId,
}: {
  groups: ImageCandidateGroupList;
  qaDiagnostics?: ImageQaDiagnosticList;
  blockId?: string | null;
  carouselSlideId?: string | null;
}) {
  const visibleGroups = groups.filter((candidateGroup) => {
    if (blockId && candidateGroup.blockId !== blockId) {
      return false;
    }

    if (carouselSlideId) {
      return candidateGroup.carouselSlide?.id === carouselSlideId;
    }

    return true;
  });
  const visibleDiagnostics = qaDiagnostics.filter(
    (diagnostic) => !blockId || !diagnostic.blockId || diagnostic.blockId === blockId,
  );
  const taskLevelDiagnostics = visibleDiagnostics.filter(
    (diagnostic) => !visibleGroups.some((group) => qaDiagnosticMatchesGroup(diagnostic, group)),
  );

  return (
    <section className="w-full space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">Image Candidates</h4>
          <p className="text-xs text-muted-foreground">
            Compare logical candidates side by side. Master and derivative assets are grouped
            together; only the linked winner renders.
          </p>
        </div>
        {visibleGroups.length > 0 ? (
          <CodeText className="rounded-full bg-muted px-2 py-1">
            {visibleGroups.reduce((sum, group) => sum + getImageCandidateComparisonCount(group), 0)}
            candidates · {visibleGroups.reduce((sum, group) => sum + group.assets.length, 0)} assets
          </CodeText>
        ) : null}
      </div>

      <QaDiagnosticsDisclosure
        diagnostics={taskLevelDiagnostics}
        title="Task-level QA diagnostics"
      />

      {visibleGroups.length === 0 ? (
        <EmptyState
          description="No image candidates have been recorded for this task yet."
          className="min-h-40 rounded-2xl bg-muted/20"
        />
      ) : (
        <div className="space-y-4">
          {visibleGroups.map((group) => (
            <ImageCandidateGroupPanel
              key={group.candidateGroupId}
              group={group}
              qaDiagnostics={visibleDiagnostics}
            />
          ))}
        </div>
      )}
    </section>
  );
}
